// utils
import { _id, _postTitles } from 'src/_mock/assets';
import { paramCase } from 'src/utils/change-case';

// ----------------------------------------------------------------------

const MOCK_ID = _id[1];

const MOCK_TITLE = _postTitles[2];

const ROOTS = {
  AUTH: '/auth',
  AUTH_DEMO: '/auth-demo',
  DASHBOARD: '/dashboard',
  MARDAM: '/mardam',
};

// ----------------------------------------------------------------------

export const paths = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
  docs: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
  zoneUI: 'https://mui.com/store/items/zone-landing-page/',
  minimalUI: 'https://mui.com/store/items/minimal-dashboard/',
  freeUI: 'https://mui.com/store/items/minimal-dashboard-free/',
  figma:
    'https://www.figma.com/file/kAYnYYdib0aQPNKZpgJT6J/%5BPreview%5D-Minimal-Web.v5.0.0?type=design&node-id=0%3A1&t=Al4jScQq97Aly0Mn-1',
  product: {
    root: `/product`,
    checkout: `/product/checkout`,
    details: (id) => `/product/${id}`,
    demo: {
      details: `/product/${MOCK_ID}`,
    },
  },
  post: {
    root: `/post`,
    details: (title) => `/post/${paramCase(title)}`,
    demo: {
      details: `/post/${paramCase(MOCK_TITLE)}`,
    },
  },
  // AUTH
  auth: {
    amplify: {
      login: `${ROOTS.AUTH}/amplify/login`,
      verify: `${ROOTS.AUTH}/amplify/verify`,
      register: `${ROOTS.AUTH}/amplify/register`,
      newPassword: `${ROOTS.AUTH}/amplify/new-password`,
      forgotPassword: `${ROOTS.AUTH}/amplify/forgot-password`,
    },
    jwt: {
      login: `${ROOTS.AUTH}/jwt/login`,
      register: `${ROOTS.AUTH}/jwt/register`,
    },
    firebase: {
      login: `${ROOTS.AUTH}/firebase/login`,
      verify: `${ROOTS.AUTH}/firebase/verify`,
      register: `${ROOTS.AUTH}/firebase/register`,
      forgotPassword: `${ROOTS.AUTH}/firebase/forgot-password`,
    },
    auth0: {
      login: `${ROOTS.AUTH}/auth0/login`,
    },
  },
  authDemo: {
    classic: {
      login: `${ROOTS.AUTH_DEMO}/classic/login`,
      register: `${ROOTS.AUTH_DEMO}/classic/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/classic/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/classic/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/classic/verify`,
    },
    modern: {
      login: `${ROOTS.AUTH_DEMO}/modern/login`,
      register: `${ROOTS.AUTH_DEMO}/modern/register`,
      forgotPassword: `${ROOTS.AUTH_DEMO}/modern/forgot-password`,
      newPassword: `${ROOTS.AUTH_DEMO}/modern/new-password`,
      verify: `${ROOTS.AUTH_DEMO}/modern/verify`,
    },
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    mail: `${ROOTS.DASHBOARD}/mail`,
    chat: `${ROOTS.DASHBOARD}/chat`,
    blank: `${ROOTS.DASHBOARD}/blank`,
    kanban: `${ROOTS.DASHBOARD}/kanban`,
    calendar: `${ROOTS.DASHBOARD}/calendar`,
    fileManager: `${ROOTS.DASHBOARD}/file-manager`,
    permission: `${ROOTS.DASHBOARD}/permission`,
    general: {
      app: `${ROOTS.DASHBOARD}/app`,
      ecommerce: `${ROOTS.DASHBOARD}/ecommerce`,
      analytics: `${ROOTS.DASHBOARD}/analytics`,
      banking: `${ROOTS.DASHBOARD}/banking`,
      booking: `${ROOTS.DASHBOARD}/booking`,
      file: `${ROOTS.DASHBOARD}/file`,
    },
    user: {
      root: `${ROOTS.DASHBOARD}/user`,
      new: `${ROOTS.DASHBOARD}/user/new`,
      list: `${ROOTS.DASHBOARD}/user/list`,
      cards: `${ROOTS.DASHBOARD}/user/cards`,
      profile: `${ROOTS.DASHBOARD}/user/profile`,
      account: `${ROOTS.DASHBOARD}/user/account`,
      edit: (id) => `${ROOTS.DASHBOARD}/user/${id}/edit`,
      demo: {
        edit: `${ROOTS.DASHBOARD}/user/${MOCK_ID}/edit`,
      },
    },
    product: {
      root: `${ROOTS.DASHBOARD}/product`,
      new: `${ROOTS.DASHBOARD}/product/new`,
      details: (id) => `${ROOTS.DASHBOARD}/product/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/product/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/product/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/product/${MOCK_ID}/edit`,
      },
    },
    invoice: {
      root: `${ROOTS.DASHBOARD}/invoice`,
      new: `${ROOTS.DASHBOARD}/invoice/new`,
      details: (id) => `${ROOTS.DASHBOARD}/invoice/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/invoice/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/invoice/${MOCK_ID}/edit`,
      },
    },
    post: {
      root: `${ROOTS.DASHBOARD}/post`,
      new: `${ROOTS.DASHBOARD}/post/new`,
      details: (title) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}`,
      edit: (title) => `${ROOTS.DASHBOARD}/post/${paramCase(title)}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}`,
        edit: `${ROOTS.DASHBOARD}/post/${paramCase(MOCK_TITLE)}/edit`,
      },
    },
    order: {
      root: `${ROOTS.DASHBOARD}/order`,
      details: (id) => `${ROOTS.DASHBOARD}/order/${id}`,
      demo: {
        details: `${ROOTS.DASHBOARD}/order/${MOCK_ID}`,
      },
    },
    job: {
      root: `${ROOTS.DASHBOARD}/job`,
      new: `${ROOTS.DASHBOARD}/job/new`,
      details: (id) => `${ROOTS.DASHBOARD}/job/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/job/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/job/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/job/${MOCK_ID}/edit`,
      },
    },
    tour: {
      root: `${ROOTS.DASHBOARD}/tour`,
      new: `${ROOTS.DASHBOARD}/tour/new`,
      details: (id) => `${ROOTS.DASHBOARD}/tour/${id}`,
      edit: (id) => `${ROOTS.DASHBOARD}/tour/${id}/edit`,
      demo: {
        details: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}`,
        edit: `${ROOTS.DASHBOARD}/tour/${MOCK_ID}/edit`,
      },
    },
  },

  // MARDAM

  mardam: {
    root: ROOTS.MARDAM,
    general: {
      app: `${ROOTS.MARDAM}/app`,
      tenants: `${ROOTS.MARDAM}/tenants`,
      tenantsCreate: `${ROOTS.MARDAM}/tenants/new`,
      users: `${ROOTS.MARDAM}/users`,
      cities: {
        cityList: `${ROOTS.MARDAM}/cities-municipalities`,
        AddCity: `${ROOTS.MARDAM}/cities-municipalities/new`,
        municipalities: {
          view: (id) => `${ROOTS.MARDAM}/municipalities/view/${id}`,
          add: (id) => `${ROOTS.MARDAM}/municipalities/new/${id}`,
        },
      },
      violations: {
        violationList: `${ROOTS.MARDAM}/violations`,
      },
      reports: `${ROOTS.MARDAM}/reports`,
      settings: `${ROOTS.MARDAM}/settings`,
      scale: `${ROOTS.MARDAM}/scale`,
      addScale: `${ROOTS.MARDAM}/scale/add`,
      editScale: `${ROOTS.MARDAM}/scale/edit`,
      hardware: `${ROOTS.MARDAM}/hardware`,
      addHardware: `${ROOTS.MARDAM}/hardware/add`,
      editHardware: `${ROOTS.MARDAM}/hardware/edit`,
      zones: `${ROOTS.MARDAM}/zones`,
      addZones: `${ROOTS.MARDAM}/zones/add`,
      editZones: `${ROOTS.MARDAM}/zones/edit`,
      geographicalLocations: `${ROOTS.MARDAM}/landfill-areas`,
      addGeographicalLocations: `${ROOTS.MARDAM}/landfill-areas/add`,
      editGeographicalLocations: `${ROOTS.MARDAM}/landfill-areas/edit`,
      typesOfWastes: `${ROOTS.MARDAM}/types-of-wastes`,
      addTypesOfWastes: `${ROOTS.MARDAM}/types-of-wastes/add`,
      editTypesOfWastes: `${ROOTS.MARDAM}/types-of-wastes/edit`,
      typesOfVehicles: `${ROOTS.MARDAM}/types-of-vehicles`,
      addTypesOfVehicles: `${ROOTS.MARDAM}/types-of-vehicles/add`,
      editTypesOfVehicles: `${ROOTS.MARDAM}/types-of-vehicles/edit`,
      contracts: `${ROOTS.MARDAM}/contracts`,
      addContracts: `${ROOTS.MARDAM}/contracts/add`,
      entryPermits: `${ROOTS.MARDAM}/entry-permits`,
      addEntryPermits: `${ROOTS.MARDAM}/entry-permits/add`,
      editEntryPermits: `${ROOTS.MARDAM}/entry-permits/edit`,
      permits: `${ROOTS.MARDAM}/permits`,
      addPermits: `${ROOTS.MARDAM}/permits/add`,
      editPermits: `${ROOTS.MARDAM}/permits/edit`,
    },
    regions: {
      regionsList: `${ROOTS.MARDAM}/regions`,
      regionCreate: `${ROOTS.MARDAM}/regions/new`,
    },
    pickup: {
      pickupPoints: `${ROOTS.MARDAM}/pickup-points`,
      pickupPointsCreate: `${ROOTS.MARDAM}/pickup-points/new`,
    },
    contractors: {
      contractors: `${ROOTS.MARDAM}/contractors`,
      contractorsCreate: `${ROOTS.MARDAM}/contractors/new`,
      contractorsVehiclesList: (id) => `${ROOTS.MARDAM}/contractors/vehicles/${id}`,
      vehicles: `${ROOTS.MARDAM}/vehicles`,
      vehiclesCreate: `${ROOTS.MARDAM}/vehicles/new`,
      superVisors: `${ROOTS.MARDAM}/supervisors`,
      superVisorsCreate: `${ROOTS.MARDAM}/supervisors/new`,
      drivers: `${ROOTS.MARDAM}/drivers`,
      driversCreate: `${ROOTS.MARDAM}/drivers/new`,
      contracts: `${ROOTS.MARDAM}/contracts`,
      contractsAdd: `${ROOTS.MARDAM}/contracts/add`,
    },
    others: {
      garbageUnloading: `${ROOTS.MARDAM}/garbage-unloading`,
      garbageReport: `${ROOTS.MARDAM}/garbage-report`,
      voilations: `${ROOTS.MARDAM}/voilations`,
    },
  },
};
